import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';

import type { UnpluginIcon } from '@adflow/frontend/ui-assets';
import { su, theme } from '@adflow/frontend/ui-theme';

const transitionDuration = '0.3s';
const knobSize = 22;
const slotBorderWidth = 1;
const slotWidth = 36;
const slotHeight = 18;

const onEdge = (slotHeight - knobSize) / 2 - slotBorderWidth;
const onFarEdge = slotWidth - knobSize - onEdge - slotBorderWidth * 2;

const knobStyle = (isLeft: boolean) =>
	css({
		position: 'absolute',

		width: knobSize,
		height: knobSize,

		top: onEdge,
		left: isLeft ? onEdge : onFarEdge,
		transition: `left ${transitionDuration}, opacity ${transitionDuration} steps(2, jump-none)`,
	});

export interface ToggleProps extends Omit<
	HTMLAttributes<HTMLButtonElement>,
	'onClick'
> {
	isOn?: boolean;
	onClick?: () => void;
	label?: string;
	leftLabel?: string;
	knobText?: string;
	onColor?: boolean;
	OnIcon?: UnpluginIcon;
	OffIcon?: UnpluginIcon;
	// Use larger padding to decrease the size of the icon
	onIconPadding?: number;
	offIconPadding?: number;
	labelStyle?: CSSInterpolation;
	leftLabelStyle?: CSSInterpolation;
	buttonStyle?: CSSInterpolation;
	testid?: string;
	disabled?: boolean;
}

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
	function ToggleRender(
		{
			isOn = false,
			onClick,
			knobText,
			label,
			leftLabel,
			labelStyle,
			leftLabelStyle = labelStyle,
			buttonStyle,
			OnIcon,
			OffIcon,
			onIconPadding = 3,
			offIconPadding = 3,
			onColor,
			testid,
			disabled,
			...buttonProps
		},
		ref
	) {
		return (
			<button
				{...buttonProps}
				ref={ref}
				data-testid={testid}
				aria-pressed={isOn ? 'true' : 'false'}
				onClick={() => {
					if (!disabled) {
						onClick?.();
					}
				}}
				css={[
					{
						display: 'flex',
						alignItems: 'center',
						cursor: disabled ? 'not-allowed' : 'pointer',
						height: '2.75rem',
						gap: 6,
					},
					buttonStyle,
				]}
				type="button"
			>
				{leftLabel ? (
					<div
						css={[
							{ color: theme.colors.palettes.primary.main },
							leftLabelStyle,
						]}
					>
						{leftLabel}
					</div>
				) : null}
				<div
					css={[
						su.roundedFull,
						{
							position: 'relative',

							width: slotWidth,
							minWidth: slotWidth, // prevent shrinking when there is not enough room
							height: slotHeight,

							marginLeft: -onEdge,
							marginRight: -onEdge,

							borderWidth: slotBorderWidth,
							borderColor: '#82D2FF',
						},
					]}
				>
					{OffIcon ? (
						<div
							css={[
								knobStyle(isOn),
								{
									padding: offIconPadding,
									color: theme.colors.text.primary,
									opacity: isOn ? 0 : 1,
								},
							]}
						>
							<OffIcon css={{ width: '100%', height: '100%' }} />
						</div>
					) : null}
					{OnIcon ? (
						<div
							css={[
								knobStyle(isOn),
								{
									padding: onIconPadding,
									color: onColor
										? theme.colors.toasts.success
										: theme.colors.text.primary,
									opacity: isOn ? 1 : 0,
								},
							]}
						>
							<OnIcon css={{ width: '100%', height: '100%' }} />
						</div>
					) : null}
					<div
						css={[
							su.textXs,
							su.roundedFull,
							{ textAlign: 'center' },
							knobStyle(!isOn),
							{
								lineHeight: `${knobSize}px`,
								backgroundColor: theme.colors.palettes.primary.main,
								color: theme.colors.palettes.primary.contrastText,
							},
						]}
						data-testid="knob"
					>
						{knobText}
					</div>
				</div>
				{label ? (
					<div
						css={[{ color: theme.colors.palettes.primary.main }, labelStyle]}
					>
						{label}
					</div>
				) : null}
			</button>
		);
	}
);
